import React from 'react';
import Icon from '@mdi/react';
import { styled } from '@mui/material/styles';
import { Typography, Grid } from '@mui/material';
import { Alert } from 'daaas-ui';
import { useTranslation } from 'react-i18next';
import {
  mdiDatabasePlus,
  mdiTablePlus,
  mdiMapLegend,
  mdiGoogleEarth,
} from '@mdi/js';
import monitorShimmer from '../assets/images/gae/monitor-shimmer.png';
import earth from '../assets/images/gae/earth.png';
import earthPlus from '../assets/images/gae/earthPlus.png';
import clipboard from '../assets/images/gae/clipboard.png';
import Banner from '../components/UI/Banner';

import ContentContainer from '../components/Layout/ContentContainer';
import ContentSection from '../components/Layout/ContentSection';
import ToolCard from '../components/UI/ToolCard';
import BrandingDAS from '../components/Layout/BrandingDAS';

const Root = styled('div')(({ theme }) => ({
  '& .banner__header': {
    fontSize: `3.5rem !important`,
    lineHeight: '1.275',
    letterSpacing: '0.47',
    [theme.breakpoints.up('sm')]: {
      fontSize: `5rem !important`,
    },
  },
}));

export default function GAE() {
  const { t, i18n } = useTranslation(['gae', 'common']);

  React.useEffect(() => {
    setTimeout(() => {
      document.title = `${t('common:daaas')} - ${t('common:gae')}`;
      document
        .querySelector("meta[name='description']")
        .setAttribute('content', t('gae:description'));
      document
        .querySelector("meta[name='keywords']")
        .setAttribute('content', t('gae:keywords'));
      document
        .querySelector("link[rel='canonical']")
        .setAttribute('href', t('gae:canonical'));
      document
        .querySelector("meta[name='dcterms.title']")
        .setAttribute('content', t('gae:dctermsTitle'));
      document
        .querySelector("meta[name='dcterms.description']")
        .setAttribute('content', t('gae:dctermsDescription'));
      document
        .querySelector("meta[name='dcterms.creator']")
        .setAttribute('content', t('common:dctermsCreator'));
      document
        .querySelector("meta[name='dcterms.language']")
        .setAttribute('content', t('common:dctermsLanguage'));
      document
        .querySelector("meta[name='dcterms.subject']")
        .setAttribute('content', t('common:dctermsSubject'));
    }, '100');
  }, [i18n.language]);

  return (
    <Root>
      <Banner>
        <Typography
          className="banner__header"
          variant="h1"
          color="textPrimary"
          sx={{ py: 2 }}
        >
          {t('common:gae')}
        </Typography>
      </Banner>
      <ContentContainer paddingBottom={false}>
        <ContentSection>
          <Typography variant="h2" sx={{ mb: 2 }}>
            {t('bannerH1')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('bannerPContent')}
          </Typography>
          <ul>
            <li>
              <Typography variant="body1">{t('bannerBullet1')}</Typography>
            </li>
            <li>
              <Typography variant="body1">{t('bannerBullet2')}</Typography>
            </li>
            <li>
              <Typography variant="body1">{t('bannerBullet3')}</Typography>
            </li>
            <li>
              <Typography variant="body1">{t('bannerBullet4')}</Typography>
            </li>
            <li>
              <Typography variant="body1">{t('bannerBullet5')}</Typography>
            </li>
            <li>
              <Typography variant="body1">{t('bannerBullet6')}</Typography>
            </li>
          </ul>
          <Alert open severity="info" closeable={false} sx={{ mt: 2 }}>
            <Typography variant="p">{t('alertText')}</Typography>
          </Alert>
        </ContentSection>
        <ContentSection
          heading={t('dataStorageHeading')}
          subheading={t('dataStorageSubHeading')}
        >
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={6} lg={4}>
              <ToolCard
                heading={{
                  text: t('azurePostgreSQLPostGISHeading'),
                }}
                links={[
                  {
                    linkTo: t('azurePostgreSQLPostGISInfoLink'),
                    text: (
                      <>
                        {t('common:documentation')}
                        <span className="screen-reader-text">
                          - {t('azurePostgreSQLPostGISHeading')}
                        </span>
                      </>
                    ),
                  },
                ]}
                icon={<Icon path={mdiDatabasePlus} />}
              >
                <Typography variant="body1">
                  {t('azurePostgreSQLPostGISParagraph')}
                </Typography>
              </ToolCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <ToolCard
                heading={{
                  text: t('azureBlobFileshareStorageHeading'),
                }}
                links={[
                  {
                    linkTo: t('azureBlobFileshareStorageInfoLink'),
                    text: (
                      <>
                        {t('common:documentation')}
                        <span className="screen-reader-text">
                          - {t('azureBlobFileshareStorageHeading')}
                        </span>
                      </>
                    ),
                  },
                ]}
                icon={<Icon path={mdiTablePlus} />}
              >
                <Typography variant="body1">
                  {t('azureBlobFileshareStorageParagraph')}
                </Typography>
              </ToolCard>
            </Grid>
          </Grid>
        </ContentSection>
        <ContentSection
          heading={t('geospatialAnalysisVisualizationAndSharingHeading')}
          subheading={t('geospatialAnalysisVisualizationAndSharingSubHeading')}
        >
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={6} lg={4}>
              <ToolCard
                heading={{
                  text: t('virtualMachineGPUHeading'),
                }}
                links={[
                  {
                    linkTo: t('virtualMachineGPUInfoLink'),
                    text: (
                      <>
                        {t('common:documentation')}
                        <span className="screen-reader-text">
                          - {t('virtualMachineGPUHeading')}
                        </span>
                      </>
                    ),
                  },
                ]}
                icon={<img src={monitorShimmer} alt="" loading="lazy" />}
              >
                <Typography variant="body1">
                  {t('virtualMachineGPUParagraph')}
                </Typography>
              </ToolCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <ToolCard
                heading={{
                  text: t('qgisHeading'),
                }}
                links={[
                  {
                    linkTo: t('qgisInfoLink'),
                    text: (
                      <>
                        {t('common:documentation')}
                        <span className="screen-reader-text">
                          - {t('qgisHeading')}
                        </span>
                      </>
                    ),
                  },
                ]}
                icon={<Icon path={mdiMapLegend} />}
              >
                <Typography variant="body1">{t('qgisParagraph')}</Typography>
              </ToolCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <ToolCard
                heading={{
                  text: t('arcGISProHeading'),
                }}
                links={[
                  {
                    linkTo: t('arcGISProInfoLink'),
                    text: (
                      <>
                        {t('common:documentation')}
                        <span className="screen-reader-text">
                          - {t('arcGISProHeading')}
                        </span>
                      </>
                    ),
                  },
                ]}
                icon={<img src={earth} alt="" loading="lazy" />}
              >
                <Typography variant="body1">
                  {t('arcGISProParagraph')}
                </Typography>
              </ToolCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <ToolCard
                heading={{
                  text: t('arcGISEnterpriseHeading'),
                }}
                links={[
                  {
                    linkTo: t('arcGISEnterpriseInfoLink'),
                    text: (
                      <>
                        {t('common:documentation')}
                        <span className="screen-reader-text">
                          - {t('arcGISEnterpriseHeading')}
                        </span>
                      </>
                    ),
                  },
                ]}
                icon={<img src={earthPlus} alt="" loading="lazy" />}
              >
                <Typography variant="body1">
                  {t('arcGISEnterpriseParagraph')}
                </Typography>
              </ToolCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <ToolCard
                heading={{
                  text: t('arcGISInsightsHeading'),
                }}
                links={[
                  {
                    linkTo: t('arcGISInsightsInfoLink'),
                    text: (
                      <>
                        {t('common:documentation')}
                        <span className="screen-reader-text">
                          - {t('arcGISInsightsHeading')}
                        </span>
                      </>
                    ),
                  },
                ]}
                icon={<Icon path={mdiGoogleEarth} />}
              >
                <Typography variant="body1">
                  {t('arcGISInsightsParagraph')}
                </Typography>
              </ToolCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <ToolCard
                heading={{
                  text: t('notebooksAndIDEsHeading'),
                }}
                links={[
                  {
                    linkTo: t('notebooksAndIDEsInfoLink'),
                    text: (
                      <>
                        {t('common:documentation')}
                        <span className="screen-reader-text">
                          - {t('notebooksAndIDEsHeading')}
                        </span>
                      </>
                    ),
                  },
                ]}
                icon={<img src={clipboard} alt="" loading="lazy" />}
              >
                <Typography variant="body1">
                  {t('notebooksAndIDEsParagraph')}
                </Typography>
              </ToolCard>
            </Grid>
          </Grid>
        </ContentSection>
      </ContentContainer>
      <BrandingDAS />
    </Root>
  );
}
